import { IS_SANDBOX_PORTAL_STORE_KEY, SANDBOX_DATA_STORE_KEY, sandboxBannerConfigStore } from './idb-cache/store';
import { UNIFIED_NAVIGATION_UI_LOGGER_NAME } from '../../../constants/logging';
import { Logger } from 'simple-logging-lib';
import { getPortalId } from '../../../utils/getPortalId';
import { fetchIsSandboxPortalHubType } from './hubsApi';
import { fetchSandboxHubsSelfData } from './sandboxHubsApi';

/**
 * Determines if the current portal is a sandbox portal.
 * Uses a cache-first strategy: checks idb-keyval cache first, then API if not cached.
 *
 * @returns Promise<boolean> - true if sandbox portal, false otherwise
 */
export const getIsSandboxPortal = async () => {
  const portalId = getPortalId();
  if (!portalId) {
    return false;
  }
  // Check cache first
  const cachedIsSandboxPortal = await sandboxBannerConfigStore.get(IS_SANDBOX_PORTAL_STORE_KEY);
  if (cachedIsSandboxPortal !== undefined) {
    return cachedIsSandboxPortal;
  }

  // If not cached, fetch from API and cache the result
  const isSandboxPortal = await fetchIsSandboxPortalHubType(portalId);
  if (isSandboxPortal !== undefined) {
    try {
      await sandboxBannerConfigStore.set(IS_SANDBOX_PORTAL_STORE_KEY, isSandboxPortal);
    } catch (error) {
      const logger = Logger.getLogger(UNIFIED_NAVIGATION_UI_LOGGER_NAME);
      logger.error('Failed to cache sandbox portal status, continuing without cache', error);
    }
  }
  return isSandboxPortal !== null && isSandboxPortal !== void 0 ? isSandboxPortal : false;
};

/**
 * Retrieves sandbox self endpoint data for the current portal.
 * Uses a cache-first strategy: checks idb-keyval cache first, then API if not cached.
 *
 * @returns Promise<SandboxSelfData | null> - sandbox self data or null if not available
 */
export const getSandboxSelfData = async () => {
  const portalId = getPortalId();
  if (!portalId) {
    return null;
  }

  // Check cache first
  const cachedSandboxData = await sandboxBannerConfigStore.get(SANDBOX_DATA_STORE_KEY);
  if (cachedSandboxData !== undefined) {
    return cachedSandboxData;
  }

  // If not cached, fetch from API and cache the result
  const sandboxSelfData = await fetchSandboxHubsSelfData(portalId);
  if (sandboxSelfData) {
    try {
      await sandboxBannerConfigStore.set(SANDBOX_DATA_STORE_KEY, {
        sandboxHubId: sandboxSelfData.sandboxHubId,
        parentHubId: sandboxSelfData.parentHubId,
        type: sandboxSelfData.type,
        version: sandboxSelfData.version
      });
    } catch (error) {
      const logger = Logger.getLogger(UNIFIED_NAVIGATION_UI_LOGGER_NAME);
      logger.error('Failed to cache sandbox self data, continuing without cache', error);
    }
  }
  return sandboxSelfData;
};